import { addDays, dateParts, diffDays, isLeapYear, isoDate, zonedParts } from "./dates.js";

/** A birthday's date in `year`; 29 February falls on 28 February in other years. */
export function birthdayInYear(birthday: string, year: number): string {
  const { month, day } = dateParts(birthday);
  if (month === 2 && day === 29 && !isLeapYear(year)) return isoDate(year, 2, 28);
  return isoDate(year, month, day);
}

/**
 * Every date of a yearly birthday within the inclusive range. With a birth year, there is
 * none before it; without one, it repeats in every year.
 */
export function birthdayDatesInRange(
  birthday: string,
  from: string,
  to: string,
  birthYear: number | null = null,
): string[] {
  const dates: string[] = [];
  const first = Math.max(dateParts(from).year, birthYear ?? 0);
  for (let year = first; year <= dateParts(to).year; year++) {
    const date = birthdayInYear(birthday, year);
    if (date >= from && date <= to) dates.push(date);
  }
  return dates;
}

export interface Span {
  id: string;
  /** Inclusive dates. */
  startDate: string;
  endDate: string;
}

export interface PlacedSpan<T extends Span = Span> {
  span: T;
  lane: number;
  /** 0-based column within the row and the number of columns it covers. */
  column: number;
  length: number;
  /** The span goes on before or after this row. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

/**
 * Places spans in a row of `days` dates (a week of the month view, the all-day section) in
 * lanes so they never overlap: earlier starts first, longer spans first. Order of the
 * input breaks ties.
 */
export function layoutSpans<T extends Span>(
  spans: readonly T[],
  rowStart: string,
  days: number,
): PlacedSpan<T>[] {
  const rowEnd = addDays(rowStart, days - 1);
  const visible = spans
    .map((span, index) => ({ span, index }))
    .filter(({ span }) => span.startDate <= rowEnd && span.endDate >= rowStart)
    .sort(
      (a, b) =>
        (a.span.startDate < b.span.startDate ? -1 : a.span.startDate > b.span.startDate ? 1 : 0) ||
        diffDays(b.span.startDate, b.span.endDate) - diffDays(a.span.startDate, a.span.endDate) ||
        a.index - b.index,
    );

  const laneEnds: number[] = []; // last occupied column per lane
  return visible.map(({ span }) => {
    const column = Math.max(0, diffDays(rowStart, span.startDate));
    const last = Math.min(days - 1, diffDays(rowStart, span.endDate));
    let lane = laneEnds.findIndex((end) => end < column);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = last;
    return {
      span,
      lane,
      column,
      length: last - column + 1,
      clippedStart: span.startDate < rowStart,
      clippedEnd: span.endDate > rowEnd,
    };
  });
}

export interface TimedBlock {
  id: string;
  /** Minutes since the start of the day. */
  start: number;
  end: number;
}

export interface PlacedBlock<T extends TimedBlock = TimedBlock> {
  block: T;
  column: number;
  columns: number;
}

/**
 * Side-by-side layout for timed events in one day column. Blocks that overlap (directly or
 * through others) form a group that shares the width; each block takes the first free
 * column. `minDuration` is the drawn minimum height, so short events that touch visually
 * are treated as overlapping too.
 */
export function layoutTimed<T extends TimedBlock>(
  blocks: readonly T[],
  minDuration = 30,
): PlacedBlock<T>[] {
  const sorted = [...blocks].sort((a, b) => a.start - b.start || b.end - a.end);
  const drawnEnd = (b: T) => Math.max(b.end, b.start + minDuration);
  const result: PlacedBlock<T>[] = [];
  let group: PlacedBlock<T>[] = [];
  let columnEnds: number[] = [];
  let groupEnd = -1;

  const closeGroup = () => {
    for (const placed of group) placed.columns = columnEnds.length;
    group = [];
    columnEnds = [];
  };

  for (const block of sorted) {
    if (block.start >= groupEnd) closeGroup();
    let column = columnEnds.findIndex((end) => end <= block.start);
    if (column === -1) column = columnEnds.length;
    columnEnds[column] = drawnEnd(block);
    groupEnd = Math.max(groupEnd, drawnEnd(block));
    const placed = { block, column, columns: 1 };
    group.push(placed);
    result.push(placed);
  }
  closeGroup();
  return result;
}

export interface DaySegment {
  date: string;
  /** Minutes since local midnight: 0–1440. */
  start: number;
  end: number;
}

/**
 * The parts of a timed event per local day: an event from Mon 22:00 to Tue 02:00 is
 * 22:00–24:00 on Monday and 00:00–02:00 on Tuesday.
 */
export function timedSegments(startsAt: Date, endsAt: Date, timeZone: string): DaySegment[] {
  const start = zonedParts(startsAt, timeZone);
  const end = zonedParts(endsAt, timeZone);
  if (start.date === end.date) {
    return [{ date: start.date, start: start.minutes, end: Math.max(end.minutes, start.minutes) }];
  }
  const segments: DaySegment[] = [{ date: start.date, start: start.minutes, end: 1440 }];
  for (let date = addDays(start.date, 1); date < end.date; date = addDays(date, 1)) {
    segments.push({ date, start: 0, end: 1440 });
  }
  if (end.minutes > 0) segments.push({ date: end.date, start: 0, end: end.minutes });
  return segments;
}
